import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export const useStreams = () => {
  const [streams, setStreams] = useState([]);
  const [editingStream, setEditingStream] = useState(null);
  const [streamForm, setStreamForm] = useState({
    title: '',
    description: '',
    embed_url: '',
    scheduled_date: '',
    timezone: 'America/Chicago',
    is_live: false,
    add_to_calendar: false,
    presenter_ids: [],
    // Paid stream fields
    is_paid: false,
    live_price: 0,
    replay_price: 0,
    stream_password: '',
    capacity: null
  });

  const fetchStreams = async () => {
    try {
      const response = await axios.get(`${API}/live-streams?per_page=100`);
      setStreams(response.data?.streams || response.data || []);
    } catch (error) {
      toast.error('Failed to load streams');
    }
  };

  const resetStreamForm = () => {
    setStreamForm({ 
      title: '', 
      description: '', 
      embed_url: '', 
      scheduled_date: '', 
      timezone: 'America/Chicago', 
      is_live: false, 
      add_to_calendar: false, 
      presenter_ids: [],
      is_paid: false,
      live_price: 0,
      replay_price: 0,
      stream_password: '',
      capacity: null
    });
    setEditingStream(null);
  };

  const handleEditStream = (stream, scrollRef) => {
    setEditingStream(stream);
    setStreamForm({
      title: stream.title,
      description: stream.description,
      embed_url: stream.embed_url,
      scheduled_date: stream.scheduled_date || '',
      timezone: stream.timezone || 'America/Chicago',
      is_live: stream.is_live,
      add_to_calendar: stream.add_to_calendar || false,
      presenter_ids: stream.presenter_ids || [],
      is_paid: stream.is_paid || false,
      live_price: stream.live_price || 0,
      replay_price: stream.replay_price || 0,
      stream_password: stream.stream_password || '',
      capacity: stream.capacity ?? null
    });
    scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveStream = async (onSuccess, presenterIds = []) => {
    try {
      const formData = { ...streamForm, presenter_ids: presenterIds };
      const streamId = editingStream?.id || editingStream;
      
      if (streamId) {
        await axios.put(`${API}/live-streams/${streamId}`, formData);
        toast.success('Stream updated!');
      } else {
        await axios.post(`${API}/live-streams`, formData);
        toast.success('Stream created!');
      }
      resetStreamForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Failed to save stream');
    }
  };

  const deleteStream = async (id, onSuccess) => {
    try {
      await axios.delete(`${API}/live-streams/${id}`);
      toast.success('Stream deleted!');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Failed to delete stream');
    }
  };

  const updateReplay = async (streamId, form, onSuccess) => {
    try {
      await axios.put(`${API}/live-streams/${streamId}/replay`, {
        has_replay: form.has_replay,
        replay_url: form.replay_url,
        replay_is_free: !!form.replay_is_free,
        replay_thumbnail: form.replay_thumbnail || null,
        replay_duration_minutes: form.replay_duration_minutes
          ? Number(form.replay_duration_minutes) : null
      });
      toast.success('Replay information updated!');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Failed to update replay');
    }
  };

  return {
    streams,
    editingStream,
    streamForm,
    setStreamForm,
    fetchStreams,
    resetStreamForm,
    handleEditStream,
    saveStream,
    deleteStream,
    updateReplay
  };
};

export default useStreams;
